// The sync engine (webdavsync-as-an-acequia-js-capability, phase 1).
//
//   plan   = await analyze(config, { onProgress, signal })
//   result = await apply(plan,    { onProgress, signal })
//   result = await run(config,    { onProgress, signal })   // both
//
// A saved WebDavSync configuration IS a config, servers resolved inline:
//   { source, destination,                       server-config records (or bare URLs)
//     options: { mirrorMode, skipLargeFiles, maxFileSize, ignoreTimestamps, forceCopyAll },
//     exclusionPatterns: [...],                  the app's rules, unchanged
//     includePatterns: [...],                    new: an allowlist
//     policy: { neverDelete, destinationOwned: [...], refuseDestinationOnSourceHost } }
//
// The decision rules are the app's, verbatim: absent at the destination →
// copy; sizes differ → copy; same size and the source newer by more than 2 s
// → copy unless ignoreTimestamps; mirror → delete what the source lacks.
// Every plan entry carries the prose `reason` the app displays and a
// machine-readable `code`, the seam the two-way project extends.
//
// Policy is enforced HERE, not in a UI, so a sync type's promises hold for
// every caller. An unreadable destination region (2026-08-11) is never
// reasoned about as empty: nothing under it is copied or deleted.

import { SyncClient } from './client.js'
import { DEFAULT_EXCLUSIONS, matchesAny, isIncluded, dirMayContainIncluded } from './patterns.js'
import { applyType } from './types.js'

export const DEFAULT_MAX_FILE_SIZE = 200 * 1024 * 1024
const TIMESTAMP_TOLERANCE_MS = 2000

export function formatFileSize(bytes) {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function normalizeServer(server, label) {
    if (!server) throw new Error(`sync: ${label} is required`)
    return typeof server === 'string' ? { url: server, authType: 'none', workingDirectory: '/' } : server
}

/**
 * How a file's bytes move (options.transfer). 'auto' tries, in order: pull
 * (COPY to the destination with `Source` — the destination fetches the file,
 * so the bytes cross its edge as a response, past Cloudflare's 100 MiB
 * request-body cap), push (COPY to the source with `Destination` — the source
 * PUTs), then client-mediated (GET → PUT through this context). A named mode
 * is that one transport only, with no fallback.
 */
export const TRANSFER_MODES = ['auto', 'pull', 'push', 'client']

/** A config with every field present. Exported so callers can see the defaults. */
export function normalizeConfig(config = {}) {
    config = applyType(config) // a named type: defaults under the config, policy as a floor
    const options = config.options || {}
    const policy = config.policy || {}
    return {
        name: config.name || null,
        type: config.type || null,
        source: normalizeServer(config.source, 'source'),
        destination: normalizeServer(config.destination, 'destination'),
        options: {
            mirrorMode: !!options.mirrorMode,
            skipLargeFiles: !!options.skipLargeFiles,
            maxFileSize: options.maxFileSize || DEFAULT_MAX_FILE_SIZE,
            ignoreTimestamps: !!options.ignoreTimestamps,
            forceCopyAll: !!options.forceCopyAll,
            transfer: TRANSFER_MODES.includes(options.transfer) ? options.transfer : 'auto',
        },
        exclusionPatterns: Array.isArray(config.exclusionPatterns)
            ? config.exclusionPatterns
            : [...DEFAULT_EXCLUSIONS],
        includePatterns: Array.isArray(config.includePatterns) ? config.includePatterns : [],
        policy: {
            neverDelete: !!policy.neverDelete,
            destinationOwned: Array.isArray(policy.destinationOwned) ? policy.destinationOwned : [],
            refuseDestinationOnSourceHost: !!policy.refuseDestinationOnSourceHost,
        },
    }
}

function parentDir(path) {
    const i = path.lastIndexOf('/')
    return i <= 0 ? null : path.slice(0, i)
}

/**
 * Is an unreadable region inside the sync's scope? A refused member carries
 * no properties, so whether it is a file or a directory is unknown: kept if
 * either reading could hold an included path, dropped if excluded. Without
 * this a region the sync never covers (a refused sibling of the included
 * folders) was reported, and warned about, as if it mattered (2026-10-06).
 */
function inScope(region, cfg) {
    const p = region.path
    if (p === '') return true
    if (matchesAny(p, cfg.exclusionPatterns) || matchesAny(p + '/', cfg.exclusionPatterns)) return false
    return isIncluded(p, cfg.includePatterns) || dirMayContainIncluded(p, cfg.includePatterns)
}

/** The unreadable region a path falls in, or undefined. */
function regionOf(path, regions) {
    return regions.find((u) => u.path === '' || path === u.path || path.startsWith(u.path + '/'))
}

/** Why a path under an unreadable region is skipped, naming the region. */
function unreadableReason(path, region, side, action) {
    const why = region.status ?? region.error ?? 'refused'
    if (region.path === path) return `The ${side} file could not be read (${why}); not proposing ${action}`
    return `${side === 'source' ? 'Source' : 'Destination'} directory ${region.path || '/'} could not be read (${why}); not proposing ${action}`
}

function makeClients(config, fetchImpl) {
    const opts = fetchImpl ? { fetch: fetchImpl } : {}
    return {
        source: new SyncClient(config.source, opts),
        destination: new SyncClient(config.destination, opts),
    }
}

/**
 * Compare two trees and say what apply() would do.
 * @param {object} config
 * @param {object} [opts]
 * @param {Function} [opts.onProgress] - (message, step, totalSteps)
 * @param {AbortSignal} [opts.signal]
 * @param {Function} [opts.fetch]
 * @returns {Promise<object>} the plan
 */
export async function analyze(config, { onProgress = null, signal = null, fetch: fetchImpl = null } = {}) {
    const cfg = normalizeConfig(config)
    const { options, policy } = cfg
    const { source, destination } = makeClients(cfg, fetchImpl)
    const progress = (message, step) => onProgress?.(message, step, 5)

    if (policy.refuseDestinationOnSourceHost && source.origin === destination.origin) {
        throw new Error(
            `sync: policy refuses a destination on the source host (${source.origin}); ` +
                `this sync type only writes to a different server`
        )
    }

    progress('Connecting to source server...', 0)
    const sourceScan = await source.listRecursive('', {
        onProgress: (m) => progress(m, 0.1),
        exclude: cfg.exclusionPatterns,
        include: cfg.includePatterns,
        signal,
    })
    progress(`Found ${sourceScan.items.length} items on source server`, 0.8)
    sourceScan.unreadable = sourceScan.unreadable.filter((u) => inScope(u, cfg))

    const afterExclude = sourceScan.items.filter((item) => !matchesAny(item.path, cfg.exclusionPatterns))
    const excludedCount = sourceScan.items.length - afterExclude.length
    const afterInclude = afterExclude.filter(
        (item) => item.isDirectory || isIncluded(item.path, cfg.includePatterns)
    )
    const notIncludedCount = afterExclude.length - afterInclude.length

    const skips = []
    const sourceFiles = []
    for (const item of afterInclude) {
        if (!item.isDirectory && options.skipLargeFiles && item.size > options.maxFileSize) {
            skips.push({ path: item.path, size: item.size, code: 'too-large', reason: `Large file skipped (${formatFileSize(item.size)})` })
            continue
        }
        if (!item.isDirectory && matchesAny(item.path, policy.destinationOwned)) {
            skips.push({ path: item.path, code: 'destination-owned', reason: 'Destination-owned by policy; never overwritten' })
            continue
        }
        sourceFiles.push(item)
    }
    {
        const parts = []
        if (excludedCount > 0) parts.push(`${excludedCount} excluded by patterns`)
        if (notIncludedCount > 0) parts.push(`${notIncludedCount} outside the include list`)
        const large = skips.filter((s) => s.code === 'too-large').length
        if (large > 0) parts.push(`${large} large files skipped`)
        progress(`Found ${sourceScan.items.length} source files${parts.length ? ` (${parts.join(', ')})` : ''}, now connecting to destination server...`, 1)
    }

    const destScan = await destination.listRecursive('', {
        onProgress: (m) => progress(m, 1.1),
        exclude: cfg.exclusionPatterns,
        include: cfg.includePatterns,
        signal,
    })
    progress(`Found ${destScan.items.length} items on destination server`, 1.8)
    destScan.unreadable = destScan.unreadable.filter((u) => inScope(u, cfg))
    // The sync's scope is the include set on both sides: a destination file
    // outside it is never compared and never deleted.
    const destFiles = destScan.items.filter(
        (item) => !matchesAny(item.path, cfg.exclusionPatterns) && (item.isDirectory || isIncluded(item.path, cfg.includePatterns))
    )
    const destMap = new Map(destFiles.map((f) => [f.path, f]))
    // Mirror deletes what the source LACKS. A source file the policy or the
    // size limit skipped is still present there — it is not an orphan.
    const sourceHas = new Set(afterInclude.filter((f) => !f.isDirectory).map((f) => f.path))

    progress('Analyzing files for differences...', 3)
    const copies = []
    let unchanged = 0
    const files = sourceFiles.filter((f) => !f.isDirectory)
    let processed = 0
    const interval = Math.min(5, Math.max(1, Math.floor(files.length / 10)))
    for (const sourceFile of files) {
        signal?.throwIfAborted?.()
        processed++
        if (onProgress && (processed % interval === 0 || processed === files.length)) {
            const shown = sourceFile.path.length > 30 ? '...' + sourceFile.path.slice(-30) : sourceFile.path
            progress(`Analyzing ${shown} (${processed}/${files.length})`, 3 + processed / files.length)
        }
        const destRegion = regionOf(sourceFile.path, destScan.unreadable)
        if (destRegion) {
            skips.push({ path: sourceFile.path, code: 'destination-unreadable', reason: unreadableReason(sourceFile.path, destRegion, 'destination', 'a copy') })
            continue
        }
        const destFile = destMap.get(sourceFile.path)
        let code = null
        let reason = ''
        if (options.forceCopyAll) {
            code = 'forced'
            reason = destFile ? `Force copying file (${formatFileSize(sourceFile.size)})` : 'File does not exist in destination (force copy all)'
        } else if (!destFile) {
            code = 'absent'
            reason = 'File does not exist in destination'
        } else if (sourceFile.size !== destFile.size) {
            code = 'size-differs'
            reason = `File sizes differ (source: ${formatFileSize(sourceFile.size)}, dest: ${formatFileSize(destFile.size)})`
        } else if (!options.ignoreTimestamps && sourceFile.lastModified && destFile.lastModified) {
            const diff = Math.abs(sourceFile.lastModified.getTime() - destFile.lastModified.getTime())
            if (diff > TIMESTAMP_TOLERANCE_MS && sourceFile.lastModified > destFile.lastModified) {
                code = 'source-newer'
                reason = `Source file is newer by ${Math.round(diff / 1000)}s (same size: ${formatFileSize(sourceFile.size)})`
            }
        }
        if (code) copies.push({ ...sourceFile, code, reason })
        else unchanged++
    }

    const deletes = []
    if (options.mirrorMode) {
        for (const destFile of destFiles) {
            if (destFile.isDirectory || sourceHas.has(destFile.path)) continue
            const srcRegion = regionOf(destFile.path, sourceScan.unreadable)
            if (srcRegion) {
                skips.push({ path: destFile.path, code: 'unscanned-region', reason: unreadableReason(destFile.path, srcRegion, 'source', 'a delete') })
                continue
            }
            if (matchesAny(destFile.path, policy.destinationOwned)) {
                skips.push({ path: destFile.path, code: 'destination-owned', reason: 'Destination-owned by policy; never deleted' })
                continue
            }
            if (policy.neverDelete) {
                skips.push({ path: destFile.path, code: 'never-delete', reason: 'Policy never deletes at the destination' })
                continue
            }
            deletes.push({ ...destFile, code: 'mirror-orphan', reason: 'File does not exist in source (mirror mode)' })
        }
    }

    const directories = new Set()
    for (const file of copies) {
        let dir = parentDir(file.path)
        while (dir) {
            directories.add(dir)
            dir = parentDir(dir)
        }
    }

    copies.sort((a, b) => a.path.localeCompare(b.path))
    deletes.sort((a, b) => a.path.localeCompare(b.path))
    progress(`Analysis complete: ${copies.length + deletes.length} operations needed`, 5)

    return {
        config: cfg,
        copies,
        directories: [...directories].sort(),
        deletes,
        skips,
        unreadable: { source: sourceScan.unreadable, destination: destScan.unreadable },
        summary: {
            sourceItems: sourceScan.items.length,
            destinationItems: destScan.items.length,
            excluded: excludedCount,
            notIncluded: notIncludedCount,
            unchanged,
            copies: copies.length,
            deletes: deletes.length,
            skips: skips.length,
        },
    }
}

/**
 * Do what a plan says. A caller may drop entries from `copies` and `deletes`
 * first — the plan table's deselect. Directories are created for what is
 * copied, then deletes run, then copies (pull, push, then GET → PUT, see
 * TRANSFER_MODES), each followed by a PROPPATCH of the source's modification
 * time where the destination allows it.
 * Throws on the first failed operation, as the app always did.
 *
 * @param {object} plan - from analyze()
 * @param {object} [opts]
 * @param {Function} [opts.onProgress] - ({ type, path, completed, total, status, ... })
 * @param {AbortSignal} [opts.signal]
 * @param {Function} [opts.fetch]
 */
export async function apply(plan, { onProgress = null, signal = null, fetch: fetchImpl = null } = {}) {
    const cfg = normalizeConfig(plan.config)
    const { source, destination } = makeClients(cfg, fetchImpl)
    const copies = [...(plan.copies || [])]
    const deletes = [...(plan.deletes || [])]

    const needed = new Set()
    for (const file of copies) {
        let dir = parentDir(file.path)
        while (dir) {
            needed.add(dir)
            dir = parentDir(dir)
        }
    }
    const directories = [...needed].sort()
    const total = directories.length + copies.length + deletes.length
    let completed = 0
    const report = (event) => onProgress?.({ completed, total, ...event })

    for (const dirPath of directories) {
        signal?.throwIfAborted?.()
        try {
            const result = await destination.createDirectoryIfNotExists(dirPath)
            completed++
            report({ type: 'directory', path: dirPath, status: 'success', message: result.created ? 'Created' : 'Already exists' })
        } catch (error) {
            report({ type: 'directory', path: dirPath, status: 'error', error: error.message })
            throw new Error(`Failed to create directory ${dirPath}: ${error.message}`)
        }
    }

    for (const file of deletes) {
        signal?.throwIfAborted?.()
        try {
            await destination.delete(file.path)
            completed++
            report({ type: 'delete', path: file.path, status: 'success' })
        } catch (error) {
            report({ type: 'delete', path: file.path, status: 'error', error: error.message })
            throw new Error(`Failed to delete file ${file.path}: ${error.message}`)
        }
    }

    let propPatch = null
    const order = transferOrder(cfg.options.transfer)
    const support = { pull: null, push: null } // null = untested, settled by the first file
    for (const file of copies) {
        signal?.throwIfAborted?.()
        if (propPatch === null) {
            propPatch = copies.some((f) => f.lastModified)
                ? await destination.testPropPatchSupport().catch((e) => ({ supported: false, error: e.message }))
                : { supported: false, error: 'no timestamps to preserve' }
        }
        try {
            const outcome = await copyWithFallback(file.path, source, destination, {
                order,
                support,
                size: file.size || 0,
                onProgress: (totalSize, transferred, done) => {
                    if (done) return
                    report({
                        type: 'file', path: file.path, status: 'transferring',
                        progress: totalSize ? Math.round((transferred / totalSize) * 100) : 0,
                        transferred, totalSize: totalSize || null,
                    })
                },
            })
            let timestampPreserved = false
            if (propPatch.supported && file.lastModified) {
                try {
                    await destination.proppatch(file.path, { getlastmodified: file.lastModified })
                    timestampPreserved = true
                } catch {
                    // copied; the timestamp is cosmetic
                }
            }
            completed++
            report({
                type: 'file', path: file.path, status: 'success', method: outcome.method,
                transferred: outcome.transferredBytes, totalSize: outcome.totalSize, timestampPreserved,
            })
        } catch (error) {
            report({ type: 'file', path: file.path, status: 'error', error: error.message })
            throw new Error(`Failed to copy file ${file.path}: ${error.message}`)
        }
    }

    return {
        directoriesCreated: directories.length,
        filesCopied: copies.length,
        filesDeleted: deletes.length,
    }
}

/** The transports 'auto' tries, in order; a named mode is itself alone. */
function transferOrder(mode) {
    return mode === 'auto' || !mode ? ['pull', 'push', 'client'] : [mode]
}

/** A response status that says "this server does not do that", not "it failed". */
const NOT_SUPPORTED = { pull: [400, 403, 405, 501, 502], push: [403, 405, 501, 502] }

/**
 * Copy one file by the first transport in `order` that works. `support`
 * remembers, for the run, whether each server-side mode is available: the
 * first file settles it (a probe), and later files go straight to the
 * settled mode. While probing, any failure of a mode — not supported, a
 * network error, or a transfer the server reported as failed — moves on to
 * the next transport; once a mode has worked, its failures are real errors.
 *
 * Pull: COPY to the destination with `Source` (HTTP-TPC pull mode). 201/204
 * is done (BrowserDAV); 202 is a report to read to the end — performance
 * markers for progress, then `success:` or `failure:` (the node server); 207
 * carries the source's status; 400/403/405/501/502 is a server without pull
 * mode (an older server answers a COPY with no Destination with 400).
 *
 * Push: COPY to the source with `Destination`. 201/204 → done; 207 → the
 * server supports it and something failed (a real error); 403/405/501/502 →
 * not supported. 502 is RFC 4918's answer to a COPY whose destination is "on
 * another server, repository, or URL namespace" that the source cannot
 * reach, with the advice to try GET/PUT. The service worker's mount proxy
 * answers it for a Destination outside the source's mount (swMountProxy.js),
 * so a sync between two mounts that carries credentials falls back instead
 * of failing.
 *
 * Both server-side modes need auth that can be forwarded to the other
 * server (tokens and basic; not cookies).
 */
async function copyWithFallback(path, source, destination, { order, support, size = 0, onProgress }) {
    let lastReason = ''
    for (let i = 0; i < order.length; i++) {
        const mode = order[i]
        const last = i === order.length - 1
        if (mode === 'client') {
            return { method: 'client-mediated', ...(await source.copyTo(path, destination, path, onProgress)) }
        }
        if (support[mode] === false) continue
        const probing = support[mode] === null
        if (probing) {
            await source._ensureAuth()
            await destination._ensureAuth()
            if (!source.hasForwardableAuth() || !destination.hasForwardableAuth()) {
                support[mode] = false
                lastReason = `${mode} needs auth that can be forwarded (a token or basic) on both sides`
                continue
            }
        }
        let outcome
        try {
            outcome = mode === 'pull'
                ? await pullOnce(path, size, source, destination, onProgress)
                : await pushOnce(path, source, destination)
        } catch (error) {
            if (!probing || last) throw error
            support[mode] = false
            lastReason = error.message
            continue
        }
        if (outcome.unsupported) {
            support[mode] = false
            lastReason = outcome.unsupported
            if (last) break
            continue
        }
        if (outcome.failed) {
            if (!probing || last) throw new Error(outcome.failed)
            support[mode] = false
            lastReason = outcome.failed
            continue
        }
        support[mode] = true
        return outcome
    }
    throw new Error(`No transfer mode could copy ${path}: ${lastReason || 'none available'}`)
}

async function pushOnce(path, source, destination) {
    const response = await source.serverCopy(path, destination, path)
    if (response.status === 201 || response.status === 204) {
        return { method: 'server-copy', status: response.status, transferredBytes: 0, totalSize: 0 }
    }
    if (response.status === 207) {
        const text = await response.text()
        if (text.includes('401') || text.includes('403')) throw new Error('Server-side COPY failed: destination returned auth error')
        throw new Error(`Server-side COPY returned 207 Multi-Status: ${text.substring(0, 200)}`)
    }
    if (NOT_SUPPORTED.push.includes(response.status)) return { unsupported: `push: source answered ${response.status}` }
    const text = await response.text().catch(() => '')
    throw new Error(`Server-side COPY failed with ${response.status}: ${text.substring(0, 200)}`)
}

async function pullOnce(path, size, source, destination, onProgress) {
    const response = await destination.serverPull(source, path, path)
    const done = (status, bytes) => ({ method: 'server-pull', status, transferredBytes: bytes, totalSize: size })
    if (response.status === 201 || response.status === 204) return done(response.status, size)
    if (response.status === 202) {
        const report = await readPullReport(response.body, { onBytes: (n) => onProgress?.(size, n, false) })
        if (report.ok) return done(202, report.bytes ?? size)
        return { failed: `Pull failed: ${report.message}` }
    }
    if (response.status === 207) {
        const text = await response.text().catch(() => '')
        return { failed: `Pull returned 207 Multi-Status: ${text.substring(0, 200)}` }
    }
    if (NOT_SUPPORTED.pull.includes(response.status)) return { unsupported: `pull: destination answered ${response.status}` }
    const text = await response.text().catch(() => '')
    throw new Error(`Pull failed with ${response.status}: ${text.substring(0, 200)}`)
}

/**
 * Read a pull's 202 report to its end: HTTP-TPC performance markers
 * (`Perf Marker` … `Stripe Bytes Transferred: N` … `End`) and the final
 * `success: …` or `failure: …` line. `body` is a ReadableStream or a string.
 * A report that ends without a verdict is a failure. Exported for tests.
 */
export async function readPullReport(body, { onBytes = null } = {}) {
    const state = { ok: null, message: '', bytes: null, markers: 0 }
    const handle = (raw) => {
        const line = raw.trimEnd()
        if (!line) return
        const m = /^Stripe Bytes Transferred:\s*(\d+)/.exec(line)
        if (m) {
            state.bytes = Number(m[1])
            onBytes?.(state.bytes)
        } else if (line === 'Perf Marker') {
            state.markers++
        } else if (line.startsWith('success:')) {
            state.ok = true
            state.message = line.slice('success:'.length).trim()
        } else if (line.startsWith('failure:')) {
            state.ok = false
            state.message = line.slice('failure:'.length).trim()
        }
    }
    let buffer = ''
    if (body && typeof body.getReader === 'function') {
        const reader = body.getReader()
        const decoder = new TextDecoder()
        for (;;) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            let nl
            while ((nl = buffer.indexOf('\n')) >= 0) {
                handle(buffer.slice(0, nl))
                buffer = buffer.slice(nl + 1)
            }
        }
        buffer += decoder.decode()
    } else {
        buffer = body == null ? '' : String(body)
    }
    for (const line of buffer.split('\n')) handle(line)
    if (state.ok === null) {
        state.ok = false
        state.message = 'the report ended without a success or failure line'
    }
    return state
}

/** analyze, then apply — unless there is nothing to do. */
export async function run(config, opts = {}) {
    const plan = await analyze(config, opts)
    if (plan.copies.length === 0 && plan.deletes.length === 0) {
        return { plan, result: { directoriesCreated: 0, filesCopied: 0, filesDeleted: 0 }, applied: false }
    }
    const result = await apply(plan, opts)
    return { plan, result, applied: true }
}

/**
 * Both ends reachable, and how much of each top level the credential reads:
 * `allValid` is reachability, `partial` that either end refused a top-level
 * member (each side's `refused` names them). A deeper refusal is the scan's
 * to find (plan.unreadable).
 */
export async function testConnections(config, { fetch: fetchImpl = null } = {}) {
    const cfg = normalizeConfig(config)
    const { source, destination } = makeClients(cfg, fetchImpl)
    const [s, d] = await Promise.all([source.testConnection(), destination.testConnection()])
    return { source: s, destination: d, allValid: s.success && d.success, partial: !!(s.partial || d.partial) }
}
